import { useContext, useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ErrorIcon from "../../components/Icons/ErrorIcon";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import WelletContext from "../../context/WelletContext";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import Footer from "../../components/Common/Footer/Footer";
import ReservationTypeCardIcon from "../../components/ReservationTypeCard/ReservationTypeCardIcon";
import MarkerIcon from "../../components/Icons/MarkerIcon";
import styles from "./Venue.module.css";
import "./filter.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import ContentApp from "../../components/ContentApp/ContentApp";
import BottomSheet from "../../components/BottomSheet/BottomSheet";
import BookSelector from "../../components/BookSelector/BookSelector";
import {
  convertTime12to24,
  formatDateTime,
  getDateFromOffset,
} from "../../helpers/dates";
import { closestIndexTo, format } from "date-fns";
import axios from "axios";
import { setReservation } from "@/actions/reservationActions";
import { lockSchedule } from "@/services/reservations/reservationsService";

const Venue = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reservation = useSelector((state) => state.reservation);
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showUrl } = useParams();
  const [show, setShow] = useState(null);
  const swiperRef = useRef(null);
  const swiperTimeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [today, setToday] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [selectedPax, setSelectedPax] = useState(
    useSelector((state) => state.reservation.paxs)
  );
  const [timeSelected, setTimeSelected] = useState(
    useSelector((state) => state.reservation.performance)
  );
  let cancelSchedulePrevious;

  const getSchedule = async (date, reservationType) => {
    setLoadingSchedule(true);
    if (
      cancelSchedulePrevious &&
      typeof cancelSchedulePrevious === "function"
    ) {
      cancelSchedulePrevious("Cancel previous schedule request");
    }

    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/GetSchedule?showId=${show.id}&date=${date}&reservationType=${reservationType}&paxs=${selectedPax}`
        // {
        //   cancelToken: new CancelToken(function executor(c) {
        //     cancelSchedulePrevious = c;
        //   }),
        // }
      );
      if (result.data.schedules.length === 0) {
        setIntervals([]);
      }

      const formattedTimes = result.data?.schedules?.map((time) => {
        const time24 = new Date(date + "T" + convertTime12to24(time.timeStart));

        return {
          time24,
          soldOut: time.isSoldOut,
          availabilityRefId: time.availabilityRefId,
          attributes: time.attributes,
        };
      });
      formattedTimes.sort((a, b) => a.time24 - b.time24);

      const allIntervals = formattedTimes.map(
        ({ time24, soldOut, availabilityRefId, attributes }) => ({
          time: time24,
          soldOut,
          availabilityRefId,
          attributes,
        })
      );

      setIntervals(allIntervals.filter((x) => !x.soldOut));

      // const rawSchedules = result.data?.schedules || [];
      // const formattedTimes = rawSchedules.map((time) => {
      //   const time24 = new Date(
      //     format(date, "yyyy-MM-dd") + "T" + convertTime12to24(time.timeStart)
      //   );
      //   if (time.timeMinutesStart >= 0 && time.timeMinutesStart <= 360)
      //     time24.setDate(time24.getDate() + 1);
      //   return {
      //     time24,
      //     soldOut: time.isSoldOut,
      //     attributes: time.attributes,
      //     originalData: time,
      //   };
      // });
      // formattedTimes.sort();
      // const allIntervals = formattedTimes.map(
      //   ({ time24, soldOut, attributes, originalData }) => ({
      //     time: time24,
      //     soldOut,
      //     attributes,
      //     originalData,
      //   })
      // );
      // setIntervals(allIntervals);
      setLoadingSchedule(false);
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/get/${showUrl}`,
        {
          params: {
            lang: language,
            details: true,
          },
        }
      );

      setShow(result.data);

      let todayDate = new Date(getDateFromOffset(result.data.utcOffset));

      const minutes = todayDate.getMinutes();
      todayDate.setMinutes(
        minutes === 0 ? minutes : minutes + (15 - (minutes % 15)),
        0,
        0
      );

      setToday(todayDate);
      if (
        !reservation.date ||
        formatDateTime(reservation.date, reservation.performance) < todayDate
      ) {
        dispatch(
          setReservation({
            ...reservation,
            date: format(todayDate, "yyyy-MM-dd"),
          })
        );
      }
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  };

  const getLocation = () => {
    const address = show.address.split(",").map((str) => str.trim());
    const textCity = show.city.nameLong.split(",").map((str) => str.trim());
    let secondLine = show.postalCode;

    if (address.length > 1) secondLine = `${address[1]}, ${secondLine}`;

    return (
      <div>
        <div>{address[0]}</div>
        <div>{secondLine}</div>
        <div>{`${textCity[0]}, ${textCity[1]}`}</div>
        <div>{textCity[2]}</div>
        {show.latitude && show.longitude && (
          <div className={styles.locationMap}>
            {/* {!mapLoaded && (
              <Skeleton
                variant='rectangular'
                sx={{
                  bgcolor: 'grey.900',
                  width: '100%',
                  height: '100%',
                  borderRadius: '.4rem',
                }}
              />
            )} */}

            <img
              className={`${mapLoaded ? "" : "d-none"}`}
              src={`https://tile1.gosurmaps.com/styles/gosur/static/${show.longitude},${show.latitude},16/400x400@2x.png`}
              onLoad={() => setMapLoaded(true)}
              alt="map"
            />
            <div className={`${styles.marker} ${mapLoaded ? "" : "d-none"}`}>
              <MarkerIcon size={32} color={"#ea4335"} />
            </div>
          </div>
        )}
      </div>
    );
  };

  const onBookSelectorChange = (e) => {
    getSchedule(e.date, "RESTAURANT", e.pax, e.time);
  };

  const onTimeClick = async (e) => {
    const performance = format(e.time, "HH:mm");
    const selectedSeatType = e.attributes[0];

    const result = await lockSchedule(welletContext, {
      pax: reservation.paxs,
      reservationDateLocalTime: reservation.date + "T" + performance,
      reservationType: "RESTAURANT",
      showId: show.id,
      attribute: selectedSeatType,
    });

    if (!result || result.success === false || !result.reservation_Token) {
      return;
    }

    const reservationInfo = {
      ...reservation,
      show: show,
      showId: show.id,
      showName: show.name,
      performance: performance,
      products: null,
      imageUrl: show.imageUrl,
    };

    dispatch(setReservation(reservationInfo));

    navigate({
      pathname: `/venue/${showUrl}/reservation-check`,
      search: `?reservationToken=${result.reservation_Token}&seats=${selectedSeatType}`,
    });
  };

  const setClosedTime = (date, intervals) => {
    if (swiperTimeRef.current && swiperTimeRef.current.swiper) {
      const index = closestIndexTo(date, intervals);
      swiperTimeRef.current.swiper.slideTo(index);
    }
  };

  const headerHtml = () => {
    return (
      <div>
        <div className="text-center">
          <h1 className="font-large fw-700 m-0">{show.name}</h1>
          <div className="text-secondary font-tiny">
            {show.address}, {show.city.name}
          </div>
        </div>
        {today && (
          <div className={`mt-3 ${styles.bookSelectorContainer}`}>
            <BookSelector
              today={today}
              lang={language}
              onChange={onBookSelectorChange}
            />
            <div className="mt-3" style={{ height: "40px" }}>
              <Swiper
                spaceBetween={10}
                slidesPerView={"auto"}
                centeredSlides={true}
                centeredSlidesBounds={true}
                ref={swiperTimeRef}
                className={styles.swiperTime}
              >
                {loadingSchedule
                  ? [...Array(5)].map((_, i) => (
                      <SwiperSlide key={i}>
                        <Skeleton
                          variant="rectangular"
                          sx={{
                            bgcolor: "#191919",
                            width: "78px",
                            borderRadius: "5px",
                            height: 38,
                          }}
                        />
                      </SwiperSlide>
                    ))
                  : intervals.length === 0
                  ? [...Array(5)].map((_, i) => (
                      <SwiperSlide key={i}>
                        <button className="button-filter empty font-small"></button>
                      </SwiperSlide>
                    ))
                  : intervals.map((item, i) => (
                      <SwiperSlide key={i}>
                        <button
                          className={`button-filter font-small ${
                            timeSelected === null ? "active" : ""
                          }`}
                          onClick={() => onTimeClick(item)}
                        >
                          {format(item.time, "h:mm aaa")}
                        </button>
                      </SwiperSlide>
                    ))}
              </Swiper>
            </div>
          </div>
        )}

        {/* <div className="mt-3">
          <ButtonCard
            title={t('shareSocialMedia')}
            description={t('shareSocialMediaDescription')}
            onClick={() => history.push('/campaigns-feed-by-show?showId=' + showId)}
          />
        </div> */}
      </div>
    );
  };

  const contentHtml = () => {
    return (
      <div
        style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
      >
        <div className={styles.hideContentIphone}></div>
        {show.description && show.description.length > 0 && (
          <div className="mt-4">
            <div className="text-primary font-extra-small text-uppercase fw-700">
              {t("about")}
            </div>
            <div
              className="text-lighter-gray font-tiny mt-1"
              dangerouslySetInnerHTML={{ __html: show.description }}
            />
          </div>
        )}

        <div className="mt-4">
          <div className="text-primary font-extra-small text-uppercase fw-700">
            {t("location")}
          </div>
          <div className="text-lighter-gray font-tiny mt-1">
            {getLocation()}
          </div>
        </div>

        {show.externalMediaUrl && (
          <div
            className={`${styles.webSiteContainer} p-3 mt-4`}
            onClick={() => window.open(show.externalMediaUrl, "_blank")}
          >
            <div className="row">
              <div className="col-auto my-auto">
                <RetinaImg
                  src="../../images/WebIcon.png"
                  alt={show.name}
                  width={24}
                  height={24}
                />
              </div>
              <div className="col text-tertiary px-0">
                <div className="font-small fw-600">
                  {t("viewCompletePresentation")}
                </div>
                <div className="font-extra-small">
                  {t("viewCompletePresentationDescription")}
                </div>
              </div>
              <div className="col-auto my-auto">
                <ChevronIconBig fillColor="var(--color-primary)" size={24} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleClick = (reservationType) => {
    navigate(
      reservationType === "restaurant"
        ? `/venue/${showUrl}/restaurant-reservation`
        : `/venue/${showUrl}/reservation/${reservationType}`,
      {
        state: { showId: show.id },
      }
    );
  };

  const renderSuccess = () => {
    return (
      <>
        <Header pathName={"/restaurant-list"} absolute={true} showBack={true} />
        <ContentApp>
          <div className={styles.pageHeader}>
            <div className={`${styles.pageSection} ${styles.header}`}>
              <div className={styles.background}>
                <Swiper
                  ref={swiperRef}
                  className={`swiper-show ${styles.swiperContainer}`}
                  pagination={true}
                  modules={[Navigation, Pagination]}
                  lazy={true}
                  style={{
                    "--swiper-pagination-top": overlayHeight + "px",
                    "--swiper-pagination-bottom": "none",
                    "--swiper-pagination-color": "#fff",
                    "--swiper-pagination-bullet-inactive-color": "#999999",
                    "--swiper-pagination-bullet-inactive-opacity": "1",
                    "--swiper-pagination-bullet-size": "7px",
                    "--swiper-pagination-bullet-horizontal-gap": "6px",
                  }}
                >
                  {show?.videos &&
                    show.videos.map((video, index) => (
                      <SwiperSlide key={`video-${index}`}>
                        <div className={styles.overlay}>
                          <video
                            webkit-playsinline="true"
                            x5-playsinline="true"
                            playsInline
                            autoPlay
                            loop
                            muted
                            src={video.videoUrl}
                            alt="media"
                            style={{ width: "100%" }}
                          />
                        </div>
                      </SwiperSlide>
                    ))}

                  <SwiperSlide>
                    <div className={styles.overlay}>
                      <img
                        src={isMobile ? show.appImageUrl : show.bannerImageUrl}
                        alt={show.name}
                      />
                    </div>
                  </SwiperSlide>

                  {show?.images &&
                    show.images.map((image, index) => (
                      <SwiperSlide key={`image-${index}`}>
                        <div className={styles.overlay}>
                          <img src={image.imageUrl} alt="media" />
                        </div>
                      </SwiperSlide>
                    ))}
                </Swiper>
              </div>
            </div>
          </div>
        </ContentApp>
        <ContentApp classes="p-0 overflow-hidden">
          <BottomSheet
            header={headerHtml()}
            content={contentHtml()}
            onSetHeight={(height) =>
              setOverlayHeight(window.innerHeight - height - 30)
            }
          />
        </ContentApp>
      </>
    );
  };

  const renderError = () => {
    return (
      <>
        <div className="success-content">
          <div className="text-center">
            <ErrorIcon color={"var(--color-primary)"} size="135px" />
          </div>
          <div className="mt-5 px-2">{t("callApiError")}</div>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (show) {
      getSchedule(format(today, "yyyy-MM-dd"), "RESTAURANT");
    }
  }, [show]);

  useEffect(() => {
    if (intervals) {
      const referenceDate = new Date(
        reservation.date +
          "T" +
          (reservation.performance
            ? reservation.performance
            : format(today, "HH:mm"))
      );
      setClosedTime(
        referenceDate,
        intervals.map((item) => item.time)
      );
    }
  }, [intervals]);

  useEffect(() => {
    getShow();
  }, []);

  return loading ? (
    <LoadingScreen />
  ) : error === null ? (
    renderSuccess()
  ) : (
    renderError()
  );
};

export default Venue;
